import { createRouter, createWebHistory, type RouteRecordRaw } from 'vue-router'
import { useAuthStore } from '@/stores/auth'
import { log } from '@/lib/logger'

const routes: RouteRecordRaw[] = [
  {
    path: '/',
    name: 'catalogue',
    component: () => import('@/views/CatalogueView.vue'),
    meta: { requiresAuth: true },
  },
  {
    path: '/label',
    name: 'label',
    component: () => import('@/views/LabelView.vue'),
    meta: { requiresAuth: true },
  },
  {
    path: '/pos',
    name: 'pos',
    component: () => import('@/views/PosView.vue'),
    meta: { requiresAuth: true },
  },
  // Returns is a mode inside the till (PosView), not its own screen. Keep the
  // old path working for any bookmark by redirecting it onto the till.
  {
    path: '/returns',
    redirect: { name: 'pos' },
  },
  {
    path: '/boxes',
    name: 'boxes',
    component: () => import('@/views/BoxesView.vue'),
    meta: { requiresAuth: true },
  },
  {
    path: '/orders',
    name: 'orders',
    component: () => import('@/views/OrdersView.vue'),
    meta: { requiresAuth: true },
  },
  {
    path: '/reports',
    name: 'reports',
    component: () => import('@/views/ReportsView.vue'),
    meta: { requiresAuth: true, requiresAdmin: true },
  },
  {
    // The audit browser moved into Rapportage as its fourth tab
    // (components/ReportsAudit.vue). The old path stays alive for bookmarks —
    // `?tab=audit` is how ReportsView knows to open THAT tab rather than the
    // default Verkopen (tabs are store state, so a bare redirect would land
    // the bookmark on the wrong panel). The reports route's own auth+admin
    // guards apply after the redirect, so this entry needs none of its own.
    path: '/audit',
    redirect: { name: 'reports', query: { tab: 'audit' } },
  },
  {
    path: '/settings',
    name: 'settings',
    component: () => import('@/views/SettingsView.vue'),
    meta: { requiresAuth: true, requiresAdmin: true },
  },
  {
    path: '/auth/callback',
    name: 'login-callback',
    component: () => import('@/views/LoginCallback.vue'),
  },
  {
    path: '/:pathMatch(.*)*',
    redirect: { name: 'catalogue' },
  },
]

export const router = createRouter({
  history: createWebHistory(),
  routes,
})

router.beforeEach(async to => {
  if (!to.meta['requiresAuth']) return true
  const authStore = useAuthStore()

  if (!authStore.isAuthenticated) {
    // Not authenticated yet — try silently first, then redirect to Hosted UI.
    await authStore.loadFromStorage()
    if (!authStore.isAuthenticated) {
      try {
        await authStore.login()
      } catch (err) {
        // login() already sets authStore.error to ERR_LOGIN_FAILED before rethrowing;
        // log here so the failure leaves a trail. The guard returns false so the
        // protected nav is cancelled — the consuming UI watches authStore.error
        // to surface the failure to the user.
        log.error('[router] login redirect failed', err)
      }
      return false
    }
  }

  // Authenticated — gate admin-only routes (e.g. /settings). A non-admin who
  // deep-links here is bounced to the catalogue rather than shown a forbidden
  // shell; the backend enforces the role independently on every write.
  if (to.meta['requiresAdmin'] && !authStore.isAdmin) {
    return { name: 'catalogue' }
  }
  return true
})
